import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileExport,
  faFileCsv,
  faFileExcel,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import { ApiCall } from "../services/ApiServices";
import { t } from "components/CentralDataMangement/translation/Translation";
import CustomNotify from "./CustomNotify";
import {
  EXPORT,
  EXPORT_STATUS,
  EXPORT_DOWNLOAD,
  EXPORT_STATE,
} from "../../../../src/routes/ApiEndpoints";

type ExportFormat = "csv" | "excel";

interface ExportFileProps {
  source: string;
  searchParams: Record<string, any>;
  microService?: string;
  endpoint?: string;
  className?: string;
}

const EXPORT_OPTIONS: { label: string; value: ExportFormat; icon: any }[] = [
  { label: "CSV", value: "csv", icon: faFileCsv },
  { label: "Excel", value: "excel", icon: faFileExcel },
];

const FILE_EXTENSIONS: Record<ExportFormat, string> = {
  csv: ".csv",
  excel: ".xlsx",
};

// ---------------------------------------------------------------------------
//  Module-scope state. Everything below this block runs OUTSIDE the React
//  component tree, so it survives page navigation (the component unmounts
//  but the JS module stays loaded). localStorage is the source of truth so
//  a hard refresh or a new tab can pick up in-flight exports.
// ---------------------------------------------------------------------------

const STORAGE_KEY = "aj.activeExports";
const POLL_INITIAL_MS = 2000;
const POLL_MAX_MS = 30000;
const POLL_TIMEOUT_MS = 30 * 60 * 1000;
// Backend keeps generated files for 24h via expires_at — entries older than
// that can't possibly resolve to a download, so drop them on read instead of
// leaving the button stuck on "Exporting...".
const ENTRY_TTL_MS = 24 * 60 * 60 * 1000;

interface StoredExport {
  exportId: string;
  microService: string;
  source: string;
  format: ExportFormat;
  downloadName: string;
  startedAt: number;
}

const readActive = (): StoredExport[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    const now = Date.now();
    const fresh = parsed.filter(
      (e: StoredExport) =>
        typeof e?.startedAt === "number" && now - e.startedAt < ENTRY_TTL_MS
    );
    // If we dropped any stale entries, write the trimmed list back so the
    // bookkeeping stays tight across page loads.
    if (fresh.length !== parsed.length) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(fresh));
      } catch {
        /* ignore — best effort */
      }
    }
    return fresh;
  } catch {
    return [];
  }
};

const writeActive = (entries: StoredExport[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // localStorage disabled / full — in-memory polling still works for this tab.
  }
};

// Same-tab notify channel. The native `storage` event only fires in OTHER
// tabs, so mounted buttons in this tab need a custom event to know when the
// active-exports list changes.
const CHANGE_EVENT = "aj.exports.changed";
const notifyChanged = () => window.dispatchEvent(new Event(CHANGE_EVENT));

const addActive = (entry: StoredExport) => {
  writeActive([...readActive().filter((e) => e.exportId !== entry.exportId), entry]);
  notifyChanged();
};

const removeActive = (exportId: string) => {
  writeActive(readActive().filter((e) => e.exportId !== exportId));
  notifyChanged();
};

// Set of exportIds with an active polling loop, so we never double-poll the
// same job (e.g. when the storage event fires in a tab that already has its
// own loop running, or when the component re-mounts).
const polling = new Set<string>();

const triggerBlobDownload = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// "follow-ups" → "Follow Ups",  "admin_fiche" → "Admin Fiche"
const humanizeSource = (s: string): string =>
  s.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Poll the backend for one export until it finishes, then trigger the
 * download + notification + cleanup. Runs at module scope so the user can
 * navigate freely while it's working.
 */
const pollExport = async (entry: StoredExport): Promise<void> => {
  if (polling.has(entry.exportId)) return;
  polling.add(entry.exportId);

  const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
  let delay = POLL_INITIAL_MS;
  const startedAt = entry.startedAt || Date.now();

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        CustomNotify({
          type: "error",
          message: t(
            `${humanizeSource(entry.source)} export timed out. Please try again.`
          ),
        });
        removeActive(entry.exportId);
        return;
      }

      await sleep(delay);
      delay = Math.min(delay * 2, POLL_MAX_MS);

      let status: any;
      try {
        status = await ApiCall.service(
          `${EXPORT_STATUS}/${entry.exportId}`,
          "GET",
          {},
          false,
          entry.microService,
          false,
          false
        );
      } catch {
        continue;
      }

      // Transient/empty response — keep trying until POLL_TIMEOUT_MS.
      if (!status || status.status >= 400) continue;

      const state: string | undefined = status?.state;

      if (state === EXPORT_STATE.READY) {
        let fileResponse: any;
        try {
          fileResponse = await ApiCall.service(
            `${EXPORT_DOWNLOAD}/${entry.exportId}`,
            "GET",
            {},
            false,
            entry.microService,
            false,
            true
          );
        } catch {
          continue;
        }

        if (!(fileResponse instanceof Blob)) {
          CustomNotify({
            type: "error",
            message: t(
              `${humanizeSource(entry.source)} export download failed.`
            ),
          });
          removeActive(entry.exportId);
          return;
        }

        // ApiCall.service blob-converts even on a 4xx JSON error response;
        // peek small blobs to make sure we're not "downloading" an error
        // payload as garbage.
        if (fileResponse.size < 4096) {
          try {
            const parsed = JSON.parse(await fileResponse.text());
            if (parsed && parsed.status && parsed.status >= 400) {
              CustomNotify({
                type: "error",
                message:
                  parsed.message ||
                  t(`${humanizeSource(entry.source)} export download failed.`),
              });
              removeActive(entry.exportId);
              return;
            }
          } catch {
            // Not JSON → genuinely small file, fall through.
          }
        }

        triggerBlobDownload(fileResponse, entry.downloadName);
        CustomNotify({
          type: "success",
          message: t(
            `Export for ${humanizeSource(entry.source)} is completed`
          ),
        });
        removeActive(entry.exportId);
        return;
      }

      if (state === EXPORT_STATE.FAILED || state === EXPORT_STATE.EXPIRED) {
        CustomNotify({
          type: "error",
          message:
            status?.error ||
            t(`${humanizeSource(entry.source)} export ${state}.`),
        });
        removeActive(entry.exportId);
        return;
      }
      // Otherwise still queued/processing — loop and poll again.
    }
  } finally {
    polling.delete(entry.exportId);
  }
};

// On module load, resume any exports that were in flight before a refresh.
// The module is imported by every page that renders <ExportFile>, so the
// resume runs as soon as the user lands anywhere with an export button.
if (typeof window !== "undefined") {
  readActive().forEach((entry) => {
    void pollExport(entry);
  });
  // Cross-tab: if another tab adds an export, pick it up here too.
  window.addEventListener("storage", (e) => {
    if (e.key !== STORAGE_KEY) return;
    readActive().forEach((entry) => {
      if (!polling.has(entry.exportId)) void pollExport(entry);
    });
  });
}

// ---------------------------------------------------------------------------
//  React component — now just a button. It fires off the queue request,
//  hands the rest off to the module-scope poller, and resets immediately.
// ---------------------------------------------------------------------------

const hasActiveExportFor = (source: string, microService: string): boolean =>
  readActive().some(
    (e) => e.source === source && e.microService === microService
  );

const ExportFile: React.FC<ExportFileProps> = ({
  source,
  searchParams,
  microService = "identity-manager",
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // Reflects whether a queued export for this (source, microService) is
  // currently in flight — derived from localStorage so it stays correct
  // after a page navigation/refresh.
  const [isExporting, setIsExporting] = useState<boolean>(() =>
    hasActiveExportFor(source, microService)
  );
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sync = () => setIsExporting(hasActiveExportFor(source, microService));
    sync();
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, [source, microService]);

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!dropdownRef.current?.contains(e.relatedTarget as Node)) {
      setIsOpen(false);
    }
  };

  const handleExport = async (format: ExportFormat) => {
    setIsOpen(false);
    if (isExporting) return; // guard against rapid double-clicks
    // Flip the button to "Exporting..." right away so the user has feedback
    // before the queue POST returns. addActive() will sync any other
    // mounted instances via the change event.
    setIsExporting(true);
    try {
      const queueResponse = await ApiCall.service(
        EXPORT,
        "POST",
        { source, format, search: searchParams },
        false,
        microService,
        false,
        false
      );

      const exportId: string | undefined = queueResponse?.exportId;
      if (!exportId) {
        setIsExporting(false);
        CustomNotify({
          type: "error",
          message:
            queueResponse?.message ||
            t("Failed to start export. Please try again."),
        });
        return;
      }

      const entry: StoredExport = {
        exportId,
        microService,
        source,
        format,
        downloadName: `${source}_export${FILE_EXTENSIONS[format]}`,
        startedAt: Date.now(),
      };

      addActive(entry);
      void pollExport(entry);
    } catch {
      setIsExporting(false);
      CustomNotify({
        type: "error",
        message: t("Failed to start export. Please try again."),
      });
    }
  };

  return (
    <div
      className={`position-relative d-inline-block ${className}`}
      ref={dropdownRef}
      onBlur={handleBlur}
    >
      <button
        type="button"
        className="btn form-button"
        onClick={() => setIsOpen((prev) => !prev)}
        disabled={isExporting}
      >
        <FontAwesomeIcon icon={faFileExport} className="me-2" />
        {isExporting ? t("Exporting...") : t("Export")}
        <FontAwesomeIcon icon={faChevronDown} className="ms-2" />
      </button>

      {!isExporting && isOpen && (
        <ul
          className="dropdown-menu show position-absolute mt-1"
          style={{ minWidth: "150px", zIndex: 1050 }}
        >
          {EXPORT_OPTIONS.map((option) => (
            <li key={option.value}>
              <button
                type="button"
                className="dropdown-item d-flex align-items-center"
                onClick={() => handleExport(option.value)}
              >
                <FontAwesomeIcon icon={option.icon} className="me-2" />
                {t(option.label)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ExportFile;
